import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Participant, Solution, CriteriaGroup } from '@/types/evaluation.types';
import { EvaluationStatus } from '@/types/evaluation.types';
import { getParticipantScorecardStatus, type ParticipantStatus } from '@/services/participants';
import { calculateScorecardCompletion } from '@/services/scoring';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Users, Settings, Search, ExternalLink, CheckCircle2, Edit2, Info } from 'lucide-react';
import { EditAssignmentsDialog } from '@/components/participants/EditAssignmentsDialog';

interface ParticipantsTableProps {
  participants: Participant[];
  evaluationId: string;
  solutions: Solution[];
  criteriaGroups: CriteriaGroup[];
  evaluationStatus: EvaluationStatus;
  onManageClick: () => void;
  onUpdateAssignments: (participantId: string, criteriaGroupAssignments: string[] | null) => void;
  demoDataButton?: React.ReactNode;
}

/**
 * Grid/table view of participants for the evaluation dashboard
 */
export function ParticipantsTable({ participants, evaluationId, solutions, criteriaGroups, evaluationStatus, onManageClick, onUpdateAssignments, demoDataButton }: ParticipantsTableProps) {
  const { t } = useTranslation(['dashboard', 'common']);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingParticipant, setEditingParticipant] = useState<Participant | null>(null);

  const getStatusBadge = (status: ParticipantStatus) => {
    switch (status) {
      case 'Pending':
        return <Badge variant="secondary">{t('common:status.pending')}</Badge>;
      case 'Accepted':
        return <Badge variant="default">{t('common:status.accepted')}</Badge>;
      case 'Declined':
        return <Badge variant="destructive">{t('common:status.declined')}</Badge>;
      case 'Scoring':
        return <Badge variant="outline" className="border-blue-500 text-blue-700 dark:text-blue-400">{t('common:status.scoring')}</Badge>;
      case 'Scorecard Submitted':
        return <Badge variant="outline" className="border-green-500 text-green-700 dark:text-green-400">{t('common:status.scorecardSubmitted')}</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getContactInfo = (participant: Participant) => {
    if (participant.email && participant.phone) {
      return `${participant.email} / ${participant.phone}`;
    }
    return participant.email || participant.phone || t('dashboard:participants.noContact');
  };

  const getAssignmentSummary = (participant: Participant) => {
    if (participant.criteriaGroupAssignments === null) {
      return t('dashboard:participants.assignments.allGroups');
    }

    if (participant.criteriaGroupAssignments.length === 0) {
      return t('dashboard:participants.assignments.noGroups');
    }

    // Get names of assigned groups
    const assignedGroups = criteriaGroups.filter(group =>
      participant.criteriaGroupAssignments?.includes(group.id)
    );

    if (assignedGroups.length === 0) {
      // Groups may have been deleted
      return t('dashboard:participants.assignments.specificGroups', { count: participant.criteriaGroupAssignments.length });
    }

    if (assignedGroups.length === 1) {
      return assignedGroups[0].name;
    }

    if (assignedGroups.length === 2) {
      return t('dashboard:participants.assignments.twoGroups', {
        first: assignedGroups[0].name,
        second: assignedGroups[1].name
      });
    }

    // 3 or more groups: show first + count
    return t('dashboard:participants.assignments.multipleGroups', {
      first: assignedGroups[0].name,
      count: assignedGroups.length - 1
    });
  };

  const handleEditAssignments = (participant: Participant) => {
    setEditingParticipant(participant);
  };

  const handleSaveAssignments = (participantId: string, criteriaGroupAssignments: string[] | null) => {
    onUpdateAssignments(participantId, criteriaGroupAssignments);
    setEditingParticipant(null);
  };

  // Filter participants based on search query
  const filteredParticipants = participants.filter((participant) => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase();
    const name = participant.name.toLowerCase();
    const email = participant.email?.toLowerCase() || '';
    const phone = participant.phone?.toLowerCase() || '';

    return name.includes(query) || email.includes(query) || phone.includes(query);
  });

  // Calculate submission progress
  const submittedCount = participants.filter((participant) => {
    const statusInfo = getParticipantScorecardStatus(participant, evaluationId, solutions);
    return statusInfo.isSubmitted;
  }).length;
  const totalParticipants = participants.length;
  const allSubmitted = submittedCount === totalParticipants && totalParticipants > 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Users className="h-5 w-5" aria-hidden="true" />
          {t('dashboard:participants.title')} ({participants.length})
        </h2>
        <div className="flex items-center gap-2">
          {demoDataButton}
          <Button onClick={onManageClick} size="sm">
            <Settings className="mr-2 h-4 w-4" aria-hidden="true" />
            {t('dashboard:participants.manageButton')}
          </Button>
        </div>
      </div>

      {/* Status-based Info Banner */}
      {evaluationStatus === EvaluationStatus.Organizing && (
        <div className="flex items-start gap-3 p-3 rounded-lg border bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
          <Info className="h-5 w-5 text-blue-700 dark:text-blue-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-blue-900 dark:text-blue-100">
              {t('dashboard:participants.organizingInfo.title')}
            </span>
            <span className="text-xs text-blue-700 dark:text-blue-300">
              {t('dashboard:participants.organizingInfo.description')}
            </span>
          </div>
        </div>
      )}

      {/* Submission Progress Banner - Active Status Only */}
      {evaluationStatus === EvaluationStatus.Active && totalParticipants > 0 && (
        <div className={`flex items-center justify-between gap-4 p-3 rounded-lg border ${
          allSubmitted
            ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800'
            : 'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800'
        }`}>
          <div className="flex items-center gap-3">
            {allSubmitted ? (
              <CheckCircle2 className="h-5 w-5 text-green-700 dark:text-green-400 flex-shrink-0" aria-hidden="true" />
            ) : (
              <Users className="h-5 w-5 text-blue-700 dark:text-blue-400 flex-shrink-0" aria-hidden="true" />
            )}
            <div className="flex flex-col">
              <span className={`text-sm font-semibold ${
                allSubmitted
                  ? 'text-green-900 dark:text-green-100'
                  : 'text-blue-900 dark:text-blue-100'
              }`}>
                {allSubmitted
                  ? t('dashboard:participants.allSubmitted')
                  : t('dashboard:participants.submissionProgress', { submitted: submittedCount, total: totalParticipants })}
              </span>
              <span className={`text-xs ${
                allSubmitted
                  ? 'text-green-700 dark:text-green-300'
                  : 'text-blue-700 dark:text-blue-300'
              }`}>
                {allSubmitted
                  ? t('dashboard:participants.allSubmittedDescription')
                  : t('dashboard:participants.submissionProgressDescription')}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <Input
          type="text"
          placeholder={t('dashboard:participants.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
          aria-label={t('dashboard:participants.searchPlaceholder')}
        />
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('dashboard:participants.columns.name')}</TableHead>
              <TableHead>{t('dashboard:participants.columns.contact')}</TableHead>
              <TableHead>{t('dashboard:participants.columns.type')}</TableHead>
              <TableHead>{t('dashboard:participants.columns.assignments')}</TableHead>
              <TableHead>{t('dashboard:participants.columns.status')}</TableHead>
              <TableHead>{t('dashboard:participants.columns.completion')}</TableHead>
              <TableHead>{t('dashboard:participants.columns.scorecard')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredParticipants.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground">
                  {searchQuery.trim() ? t('common:search.noResults') : t('dashboard:participants.empty')}
                </TableCell>
              </TableRow>
            ) : (
              filteredParticipants.map((participant) => {
                const statusInfo = getParticipantScorecardStatus(participant, evaluationId, solutions);
                const completionInfo = calculateScorecardCompletion(participant, evaluationId, solutions, criteriaGroups);

                return (
                  <TableRow key={participant.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {participant.name}
                        {statusInfo.isSubmitted && (
                          <CheckCircle2
                            className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0"
                            aria-label={t('dashboard:participants.scorecardSubmittedIndicator')}
                          />
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {getContactInfo(participant)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={participant.participantType === 'Host' ? 'default' : 'outline'}>
                        {t(`common:participantType.${participant.participantType.toLowerCase()}`)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">
                          {getAssignmentSummary(participant)}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => handleEditAssignments(participant)}
                          aria-label={t('dashboard:participants.editAssignments', { name: participant.name })}
                        >
                          <Edit2 className="h-3.5 w-3.5" aria-hidden="true" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(statusInfo.status)}</TableCell>
                    <TableCell>
                      <div className="space-y-1 min-w-[120px]">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium tabular-nums">{completionInfo.percentage}%</span>
                          <span className="text-xs text-muted-foreground">
                            {completionInfo.scoredCount}/{completionInfo.totalCount}
                          </span>
                        </div>
                        <Progress value={completionInfo.percentage} className="h-2" />
                      </div>
                    </TableCell>
                    <TableCell>
                      {statusInfo.hasScorecard && statusInfo.scorecardLink ? (
                        <Button variant="link" size="sm" className="h-auto p-0" asChild>
                          <Link to={statusInfo.scorecardLink} className="flex items-center gap-1">
                            {t('dashboard:participants.viewScorecard')}
                            <ExternalLink className="h-3 w-3" aria-hidden="true" />
                          </Link>
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground italic">
                          {t('dashboard:participants.scoringNotStarted')}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Edit Assignments Dialog */}
      <EditAssignmentsDialog
        participant={editingParticipant}
        criteriaGroups={criteriaGroups}
        isOpen={editingParticipant !== null}
        onClose={() => setEditingParticipant(null)}
        onSave={handleSaveAssignments}
      />
    </div>
  );
}
